import EventEmitter from 'eventemitter3';

/**
 * Map describing an event contract: event name -> listener signature.
 *
 * Example:
 * ```ts
 * interface MyEvents {
 *   changed: () => void;
 *   error: (err: Error) => void;
 * }
 * ```
 */
export type EventMap = { [event: string]: (...args: any[]) => void };

/** Function returned by `on()` that removes the listener when called. Idempotent. */
export type Unsubscribe = () => void;

/**
 * Thin, strongly-typed wrapper around `eventemitter3`.
 *
 * Why this exists:
 * - `eventemitter3` is untyped; callbacks receive `any[]` arguments.
 * - We want `on()` to return an unsubscribe function so callers can
 *   use the standard `useEffect(() => unsubscribe, [])` cleanup pattern
 *   without separately tracking listener references.
 *
 * Usage:
 * ```ts
 * const emitter = new TypedEmitter<MyEvents>();
 * const off = emitter.on('changed', () => { ... });
 * off();  // stop listening
 * ```
 */
export class TypedEmitter<E extends EventMap> {
  private readonly _inner = new EventEmitter();

  /**
   * Subscribe to `event`. Returns a function that, when called, removes the
   * listener. The returned function is idempotent — calling it more than once
   * is safe.
   */
  on<K extends keyof E & string>(event: K, listener: E[K]): Unsubscribe {
    this._inner.on(event, listener);
    let isRemoved = false;
    return () => {
      if (isRemoved) return;
      isRemoved = true;
      this._inner.off(event, listener);
    };
  }

  /**
   * Emit `event` with the listener's expected arguments.
   */
  emit<K extends keyof E & string>(event: K, ...args: Parameters<E[K]>): void {
    this._inner.emit(event, ...args);
  }

  /**
   * Remove all listeners. Used by service tests and, where appropriate, by
   * service `close*` methods to prevent leaks.
   */
  removeAllListeners(): void {
    this._inner.removeAllListeners();
  }
}
